import { useState } from 'react';
import { ArrowLeft, Pencil, ShoppingCart, Heart, Camera } from 'lucide-react';
import nhost from '../nhost';
import { avg } from '../helpers';
import { awardPoints } from '../points';
import WineEditModal from './WineEditModal';

const TYPE = {
  'Rött':        { dot: '#8B2A3E', bg: '#F5E8EB', fg: '#5C1A28' },
  'Vitt':        { dot: '#A07830', bg: '#F7F0E6', fg: '#7D5A22' },
  'Rosé':        { dot: '#C4566A', bg: '#FCE8EF', fg: '#A03060' },
  'Mousserande': { dot: '#356BB3', bg: '#E8F0FC', fg: '#234E8C' },
};

function Stars({ rating, size = 13 }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', fontSize: size, letterSpacing: 2, lineHeight: 1 }}>
      <span style={{ color: 'rgba(60,20,30,.18)' }}>★★★★★</span>
      <span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', whiteSpace: 'nowrap', color: 'var(--gold)', width: `${rating / 5 * 100}%` }}>★★★★★</span>
    </span>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 2, padding: '4px 0' }}>
      {[1,2,3,4,5].map(pos => {
        const isFull = value >= pos;
        const isHalf = !isFull && value >= pos - 0.5;
        return (
          <div key={pos} style={{ position: 'relative', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 36, color: (isFull || isHalf) ? 'var(--wine)' : 'rgba(60,20,30,.18)', opacity: isHalf ? 0.55 : 1, userSelect: 'none', pointerEvents: 'none' }}>★</span>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', cursor: 'pointer' }}>
              <div style={{ flex: 1 }} onClick={() => onChange(pos - 0.5)} />
              <div style={{ flex: 1 }} onClick={() => onChange(pos)} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function WineDetail({ wine, wines, profile, onBack, onRefresh }) {
  const myRating = wine.ratings.find(r => r.user_id === profile.id);

  const [star, setStar] = useState(myRating?.rating || 0);
  const [comment, setComment] = useState(myRating?.comment || '');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const times = wines.filter(x => x.name === wine.name).length;
  const a = avg(wine.ratings);
  const ratingNum = parseFloat(a) || 0;
  const tm = TYPE[wine.type] || { dot: '#8B2A3E', bg: '#F5E8EB', fg: '#5C1A28' };
  const isWineBox = wine.session?.event?.event_type === 'wine_box';
  const isInterested = wine.interests?.some(i => i.user_id === profile.id);

  async function toggleInterest() {
    const existing = wine.interests?.find(i => i.user_id === profile.id);
    if (existing) {
      await nhost.graphql.request(`mutation { delete_wine_interests_by_pk(id: "${existing.id}") { id } }`);
    } else {
      await nhost.graphql.request(`mutation { insert_wine_interests_one(object: { wine_id: "${wine.id}", user_id: "${profile.id}" }) { id } }`);
      await awardPoints(profile.id, 'wine_interest', wine.id);
    }
    onRefresh();
  }

  async function saveRating() {
    if (!star) { alert('Välj ett betyg'); return; }
    setSaving(true);
    await nhost.graphql.request(`
      mutation AddRating($wine_id: uuid!, $user_id: uuid!, $rating: numeric!, $comment: String) {
        insert_ratings_one(object: { wine_id: $wine_id, user_id: $user_id, rating: $rating, comment: $comment },
          on_conflict: { constraint: ratings_wine_id_user_id_key, update_columns: [rating, comment] }) { id }
      }
    `, { wine_id: wine.id, user_id: profile.id, rating: star, comment });
    await awardPoints(profile.id, 'rate_wine', wine.id);
    if (comment?.trim()) await awardPoints(profile.id, 'comment_wine', wine.id);
    setSaving(false);
    setEditing(false);
    onRefresh();
    onBack();
  }

  function buyButtonLabel() {
    const url = wine.source_url || '';
    if (url.includes('systembolaget')) return 'Köp på Systembolaget';
    if (url.includes('vivino')) return 'Visa på Vivino';
    if (url.includes('winefinder')) return 'Visa på Winefinder';
    return 'Köp / Visa vinet';
  }

  const cardStyle = {
    background: '#fff',
    border: '1px solid rgba(60,20,30,.08)',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
  };

  return (
    <div>
      {/* Header */}
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button
          onClick={onBack}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--wine)', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}
        >
          <ArrowLeft size={18} strokeWidth={1.9} />
          Tillbaka
        </button>
        <button
          onClick={() => setShowEditModal(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--wine-light)', border: 'none', color: 'var(--wine)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: '6px 12px', borderRadius: 20 }}
        >
          <Pencil size={14} strokeWidth={1.8} />
          Redigera
        </button>
      </div>

      <div className="screen" style={{ paddingTop: 4 }}>

        {/* Hero */}
        <div style={{ background: tm.bg, borderRadius: 20, padding: '22px 18px', textAlign: 'center', marginBottom: 12 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 27, fontWeight: 600, color: 'var(--wine-dark)', lineHeight: 1.05 }}>{wine.name}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>
            {[wine.grape, wine.vintage].filter(Boolean).join(' · ')}
          </div>
          {(wine.region || wine.country) && (
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              {[wine.region, wine.country].filter(Boolean).join(', ')}
            </div>
          )}

          {/* Chips */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            {wine.type && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', color: tm.fg, fontSize: 11, padding: '4px 11px', borderRadius: 20 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: tm.dot }} />
                {wine.type}
              </span>
            )}
            {wine.session?.profile && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--teal-light)', color: 'var(--teal)', fontSize: 11, padding: '4px 11px', borderRadius: 20 }}>
                {isWineBox ? 'Vinlåda' : 'Provning'} av {wine.session.profile.display_name}
              </span>
            )}
            {times > 1 && (
              <span style={{ background: '#EAF3DE', color: '#3B6D11', fontSize: 11, padding: '4px 11px', borderRadius: 20 }}>
                Provats {times} gånger
              </span>
            )}
          </div>

          {/* Flaskbild */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            {wine.image_url ? (
              <div style={{ width: 104, height: 136, borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(139,42,62,.15)' }}>
                <img src={wine.image_url} alt={wine.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ) : (
              <div style={{
                width: 104, height: 136, borderRadius: 14,
                border: '1.5px dashed rgba(139,42,62,.4)',
                background: 'repeating-linear-gradient(45deg,#fff,#fff 7px,#faf1f3 7px,#faf1f3 14px)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 7,
                color: 'var(--wine)',
              }}>
                <Camera size={24} strokeWidth={1.7} />
                <span style={{ fontSize: 11.5, fontWeight: 500 }}>Lägg till bild</span>
                <span style={{ fontSize: 9, color: 'var(--muted)' }}>av flaskan</span>
              </div>
            )}
          </div>

          {/* Betyg */}
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 50, fontWeight: 300, color: tm.fg, lineHeight: 1, marginTop: 18 }}>{a}</div>
          {ratingNum > 0 && (
            <div style={{ marginTop: 6 }}>
              <Stars rating={ratingNum} size={18} />
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>Snitt av {wine.ratings.length} betyg</div>

          {/* Köpknapp */}
          {wine.source_url && (
            <a
              href={wine.source_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 18,
                background: tm.dot, color: '#fff',
                padding: '11px 22px', borderRadius: 24,
                textDecoration: 'none', fontSize: 13.5, fontWeight: 500,
              }}
            >
              <ShoppingCart size={17} strokeWidth={1.8} />
              {buyButtonLabel()}
            </a>
          )}
        </div>

        {/* Vininfo */}
        <div style={cardStyle}>
          <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.8px' }}>Vininfo</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginTop: 12 }}>
            {wine.grape && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Druva</div>
                <div style={{ fontSize: 14, marginTop: 3 }}>{wine.grape}</div>
              </div>
            )}
            {wine.country && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Land</div>
                <div style={{ fontSize: 14, marginTop: 3 }}>{wine.country}</div>
              </div>
            )}
            {wine.region && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Region</div>
                <div style={{ fontSize: 14, marginTop: 3 }}>{wine.region}</div>
              </div>
            )}
            {wine.vintage && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Årgång</div>
                <div style={{ fontSize: 14, marginTop: 3 }}>{wine.vintage}</div>
              </div>
            )}
            {wine.price && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Pris</div>
                <div style={{ fontSize: 14, marginTop: 3 }}>{wine.price} kr</div>
              </div>
            )}
            {wine.type && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Typ</div>
                <div style={{ fontSize: 14, marginTop: 3 }}>{wine.type}</div>
              </div>
            )}
            {wine.packaging && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Förpackning</div>
                <div style={{ fontSize: 14, marginTop: 3 }}>{wine.packaging}</div>
              </div>
            )}
          </div>
        </div>

        {/* Intresse */}
        <div style={{ ...cardStyle, padding: '13px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {wine.interests?.map(i => (
              <div key={i.id} title={i.profile?.display_name} style={{
                width: 26, height: 26, borderRadius: '50%', background: 'var(--wine-light)',
                border: '2px solid #fff', marginRight: -8, overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 600, color: 'var(--wine)', fontFamily: 'var(--font-display)',
              }}>
                {i.profile?.avatar_url ? <img src={i.profile.avatar_url} alt={i.profile?.display_name || ''} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : i.profile?.display_name?.[0]}
              </div>
            ))}
            <div style={{ fontSize: 12, color: 'var(--muted)', marginLeft: wine.interests?.length ? 16 : 0 }}>
              {wine.interests?.length
                ? `${wine.interests.length} ${wine.interests.length === 1 ? 'intresserad' : 'intresserade'}`
                : 'Ingen har visat intresse än'
              }
            </div>
          </div>
          <button
            onClick={toggleInterest}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12.5, fontWeight: 500,
              background: isInterested ? 'var(--wine-light)' : 'white',
              border: 'none', color: isInterested ? 'var(--wine)' : 'var(--muted)',
              cursor: 'pointer', padding: '7px 14px', borderRadius: 20, fontFamily: 'inherit',
            }}
          >
            <Heart size={14} strokeWidth={1.8} fill={isInterested ? 'currentColor' : 'none'} />
            {isInterested ? 'Intresserad' : 'Visa intresse'}
          </button>
        </div>

        {/* Smakprofil */}
        {wine.profile && (
          <div style={cardStyle}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.8px' }}>Smakprofil</div>
            <div style={{ background: 'var(--cream)', border: '1px solid rgba(60,20,30,.06)', borderRadius: 12, padding: '13px 15px', marginTop: 10 }}>
              {wine.profile_source && (
                <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{wine.profile_source}</div>
              )}
              <div style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 15, lineHeight: 1.55, marginTop: wine.profile_source ? 6 : 0 }}>{wine.profile}</div>
            </div>
          </div>
        )}

        {/* Serverades med */}
        {wine.session?.food && (
          <div style={cardStyle}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.8px' }}>Serverades med</div>
            <div style={{ fontSize: 14, color: 'var(--muted)', marginTop: 6 }}>{wine.session.food}</div>
          </div>
        )}

        {/* Betyg & kommentarer */}
        <div style={cardStyle}>
          <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.8px' }}>Betyg & kommentarer</div>
          {wine.ratings.length === 0 && <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>Inga betyg ännu</div>}
          {wine.ratings.map((r, idx) => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              padding: '11px 0',
              borderBottom: idx < wine.ratings.length - 1 ? '1px solid rgba(60,20,30,.07)' : 'none',
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{r.profile?.display_name}</div>
                {r.comment && <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', marginTop: 2 }}>"{r.comment}"</div>}
              </div>
              <div style={{ flexShrink: 0, marginLeft: 8 }}>
                <Stars rating={r.rating} size={14} />
              </div>
            </div>
          ))}
        </div>

        {/* Ditt betyg – nytt */}
        {!myRating && (
          <div style={cardStyle}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.8px' }}>Ditt betyg</div>
            <StarPicker value={star} onChange={setStar} />
            {star > 0 && <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{star} / 5</div>}
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Dina smaknoter för detta tillfälle (valfritt)…"
              style={{
                width: '100%', boxSizing: 'border-box', minHeight: 70,
                border: '1px solid rgba(139,42,62,.2)', borderRadius: 12,
                padding: '11px 13px', fontSize: 13.5, color: 'var(--ink)',
                background: '#fff', outline: 'none', fontFamily: 'inherit',
                resize: 'none', marginTop: 12,
              }}
            />
            <button
              onClick={saveRating}
              disabled={saving}
              style={{ width: '100%', marginTop: 12, background: 'var(--wine)', color: '#fff', border: 'none', borderRadius: 12, padding: 13, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {saving ? 'Sparar...' : 'Spara betyg'}
            </button>
          </div>
        )}

        {/* Befintligt betyg – visa */}
        {myRating && !editing && (
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.8px' }}>Ditt betyg</div>
              <button
                onClick={() => setEditing(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--wine-light)', border: 'none', color: 'var(--wine)', fontSize: 12, padding: '5px 12px', borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <Pencil size={12} strokeWidth={1.8} />
                Ändra
              </button>
            </div>
            <div style={{ marginTop: 10 }}>
              <Stars rating={myRating.rating} size={20} />
            </div>
            {myRating.comment && (
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6, fontStyle: 'italic' }}>"{myRating.comment}"</div>
            )}
          </div>
        )}

        {/* Befintligt betyg – ändra */}
        {myRating && editing && (
          <div style={cardStyle}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.8px' }}>Ändra ditt betyg</div>
            <StarPicker value={star} onChange={setStar} />
            {star > 0 && <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{star} / 5</div>}
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Dina smaknoter…"
              style={{
                width: '100%', boxSizing: 'border-box', minHeight: 70,
                border: '1px solid rgba(139,42,62,.2)', borderRadius: 12,
                padding: '11px 13px', fontSize: 13.5, color: 'var(--ink)',
                background: '#fff', outline: 'none', fontFamily: 'inherit',
                resize: 'none', marginTop: 12,
              }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button
                onClick={() => { setEditing(false); setStar(myRating.rating); setComment(myRating.comment || ''); }}
                style={{ flex: 1, background: 'none', border: '1px solid rgba(139,42,62,.2)', borderRadius: 12, padding: 13, fontSize: 14, color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Avbryt
              </button>
              <button
                onClick={saveRating}
                disabled={saving}
                style={{ flex: 1, background: 'var(--wine)', color: '#fff', border: 'none', borderRadius: 12, padding: 13, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {saving ? 'Sparar...' : 'Spara ändringar'}
              </button>
            </div>
          </div>
        )}
      </div>

      {showEditModal && (
        <WineEditModal
          wine={wine}
          isAdmin={profile.is_admin}
          onClose={() => setShowEditModal(false)}
          onSaved={() => { setShowEditModal(false); onRefresh(); onBack(); }}
        />
      )}
    </div>
  );
}
